/* BRACKET AUDIO — broadcast cues for the championship tree. A synthesized
   rising whoosh rides with each traveling winner, a three-note brass sting
   opens the round-of-8 ceremony, and the arena's crowd-roar sample answers
   a coronation. Everything fails silent — sound is seasoning, never a blocker. */
let audioCtx = null;

const getCtx = () => {
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    audioCtx = new AC();
  }
  if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
  return audioCtx;
};

const noiseBuffer = (ctx, seconds) => {
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  return buf;
};

/* rising band-swept noise — the sound of a winner moving up the tree */
export const playWhoosh = (delaySec = 0) => {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    const t0 = ctx.currentTime + delaySec;
    const dur = 0.7;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, dur);
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.Q.value = 1.4;
    filter.frequency.setValueAtTime(220, t0);
    filter.frequency.exponentialRampToValueAtTime(3800, t0 + dur);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.22, t0 + dur * 0.55);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(filter).connect(gain).connect(ctx.destination);
    src.start(t0);
    src.stop(t0 + dur);
  } catch {
    /* audio blocked — silence is fine */
  }
};

/* broadcast sting — low hit, then a rising G-D-G triad. opens the ceremony */
export const playSting = () => {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const hit = ctx.createBufferSource();
    hit.buffer = noiseBuffer(ctx, 0.25);
    const hitFilter = ctx.createBiquadFilter();
    hitFilter.type = "lowpass";
    hitFilter.frequency.value = 320;
    const hitGain = ctx.createGain();
    hitGain.gain.setValueAtTime(0.0001, t0);
    hitGain.gain.exponentialRampToValueAtTime(0.3, t0 + 0.02);
    hitGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
    hit.connect(hitFilter).connect(hitGain).connect(ctx.destination);
    hit.start(t0);
    hit.stop(t0 + 0.25);
    [
      [196, 0.06],
      [293.66, 0.2],
      [392, 0.34],
    ].forEach(([freq, dt]) => {
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0 + dt);
      g.gain.exponentialRampToValueAtTime(0.14, t0 + dt + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.5);
      osc.connect(g).connect(ctx.destination);
      osc.start(t0 + dt);
      osc.stop(t0 + dt + 0.55);
    });
  } catch {
    /* audio blocked — silence is fine */
  }
};

/* the dynasty horn — a deep detuned air-horn blast for back-to-back crowns */
export const playHorn = () => {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    [
      [110, 0],
      [116.5, 0],
      [220, 0.05],
    ].forEach(([freq, dt]) => {
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0 + dt);
      g.gain.exponentialRampToValueAtTime(0.11, t0 + dt + 0.08);
      g.gain.setValueAtTime(0.11, t0 + dt + 0.7);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 1.4);
      osc.connect(g).connect(ctx.destination);
      osc.start(t0 + dt);
      osc.stop(t0 + dt + 1.45);
    });
  } catch {
    /* silence is fine */
  }
};

/* the arena erupts — sample already ships with the game */
export const playRoar = (delayMs = 0) => {
  window.setTimeout(() => {
    try {
      const audio = new Audio("/assets/crowd-roar.wav");
      audio.volume = 0.6;
      audio.play().catch(() => {});
    } catch {
      /* silence is fine */
    }
  }, delayMs);
};
